import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Play, ChevronDown, ChevronRight, Ban } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { crumbsFor } from '../../navigation/modules';
import api from '../../services/api';
import { useDialogs } from '../../components/Dialogs';
import { field, ghost, primary, muted, card, rowBorder, errorText } from '../time/styles';
import { Message } from '../time/ui';
import TaskPanel from './TaskPanel';
import JoinersPanel from './JoinersPanel';
import ExitsPanel from './ExitsPanel';
import { TaskStatus } from './taskUi';
import { dueText } from './docUtils';

// HR/Admin → Joiners & leavers: start an onboarding or exit checklist for an
// employee, follow progress, review what people submitted.

const KIND = { ONBOARDING: 'Onboarding', OFFBOARDING: 'Exit' };

const StartForm = ({ users, onStarted, say }) => {
    const [f, setF] = useState({ userId: '', kind: 'ONBOARDING', templateId: '', anchorDate: '', buddyId: '' });
    const [templates, setTemplates] = useState([]);
    const [hint, setHint] = useState(null);
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setF(p => ({ ...p, [k]: v }));
    useEffect(() => {
        api.get('/lifecycle/checklists').then(r => setTemplates(r.data.templates.filter(t => t.isActive))).catch(e => say(false, errorText(e, 'Could not load checklists')));
    }, [say]);
    // Suggest the checklist and date for the chosen person.
    useEffect(() => {
        if (!f.userId) { setHint(null); return undefined; }
        let live = true;
        api.get('/lifecycle/cases/suggest', { params: { userId: f.userId, kind: f.kind } }).then(r => {
            if (!live) return;
            setHint(r.data);
            setF(p => ({ ...p, templateId: r.data.templateId || '', anchorDate: r.data.anchorDate || '' }));
        }).catch(() => {});
        return () => { live = false; };
    }, [f.userId, f.kind]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const r = await api.post('/lifecycle/cases', { ...f, buddyId: f.buddyId || undefined });
            say(true, `Started the ${f.kind === 'ONBOARDING' ? 'onboarding' : 'exit'} checklist for ${users.find(u => u.id === f.userId)?.name}. Everyone with a task has been emailed.`);
            onStarted(r.data.id);
        } catch (err) {
            say(false, errorText(err, 'Could not start the checklist'));
        } finally {
            setBusy(false);
        }
    };
    const options = templates.filter(t => t.kind === f.kind);
    if (!templates.length) return <p style={muted}>No checklists yet. Create them in <Link to="/admin/lifecycle?tab=checklists">Lifecycle setup → Checklists</Link>.</p>;
    const none = options.length === 0;
    return (
        <form onSubmit={submit} style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Checklist for</span>
                    <select aria-label="Checklist kind" style={field} value={f.kind} onChange={e => setF(p => ({ ...p, kind: e.target.value, templateId: '', anchorDate: '' }))}>
                        <option value="ONBOARDING">Joining</option>
                        <option value="OFFBOARDING">Leaving</option>
                    </select>
                </label>
                <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Employee</span>
                    <select aria-label="Employee" required style={field} value={f.userId} onChange={e => set('userId', e.target.value)}>
                        <option value="">Choose…</option>
                        {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                </label>
                <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Checklist</span>
                    <select aria-label="Checklist" required style={field} value={f.templateId} onChange={e => set('templateId', e.target.value)}>
                        <option value="">Choose…</option>
                        {options.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                </label>
                <label style={{ display: 'grid', gap: 4 }}><span style={muted}>{f.kind === 'ONBOARDING' ? 'Joining date' : 'Last working day'}</span>
                    <input type="date" aria-label={f.kind === 'ONBOARDING' ? 'Joining date' : 'Last working day'} required style={field} value={f.anchorDate} onChange={e => set('anchorDate', e.target.value)} />
                </label>
                {f.kind === 'ONBOARDING' && (
                    <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Buddy (optional)</span>
                        <select aria-label="Buddy" style={field} value={f.buddyId} onChange={e => set('buddyId', e.target.value)}>
                            <option value="">None</option>
                            {users.filter(u => u.id !== f.userId && u.isActive !== false).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                        </select>
                    </label>
                )}
            </div>
            {none && <p role="alert" style={{ margin: 0, color: '#d97706' }}>No {f.kind === 'ONBOARDING' ? 'onboarding' : 'exit'} checklist is in use. Create or switch one on in <Link to="/admin/lifecycle?tab=checklists">Lifecycle setup → Checklists</Link>.</p>}
            {hint && (
                <p style={{ ...muted, margin: 0 }}>
                    {hint.openCaseId ? 'This person already has one in progress (below). ' : ''}
                    {hint.manager ? `Manager tasks go to ${hint.manager}.` : 'No reporting manager is set, so manager tasks go to HR.'}
                </p>
            )}
            <div><button className="btn btn-primary" style={primary} disabled={busy || none}><Play size={14} /> {busy ? 'Starting…' : 'Start checklist'}</button></div>
        </form>
    );
};

const CaseDetail = ({ id, say, onChanged }) => {
    const [c, setC] = useState(null);
    const [openId, setOpenId] = useState(null);
    const [version, setVersion] = useState(0);
    useEffect(() => {
        let live = true;
        api.get(`/lifecycle/cases/${id}`).then(r => {
            if (!live) return;
            setC(prev => {
                if (prev && prev.status === 'OPEN' && r.data.status === 'COMPLETED') say(true, `Checklist complete: ${r.data.user.name}`);
                return r.data;
            });
        }).catch(e => { if (live) say(false, errorText(e, 'Could not load the checklist')); });
        return () => { live = false; };
    }, [id, version, say]);
    const { ask, dialogs } = useDialogs();
    const refresh = () => { setVersion(v => v + 1); onChanged(); };
    const cancel = async () => {
        const reason = await ask({ title: 'Cancel this checklist?', message: 'Open tasks are dropped.', required: true, confirmText: 'Cancel checklist' });
        if (reason === null) return;
        try {
            await api.post(`/lifecycle/cases/${id}/cancel`, { reason });
            say(true, 'Checklist cancelled');
            refresh();
        } catch (e) {
            say(false, errorText(e, 'Could not cancel'));
        }
    };
    if (!c) return <p style={muted}>Loading…</p>;
    return (
        <div style={{ padding: '4px 0 12px 24px' }}>
            {dialogs}
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {c.tasks.map(t => (
                    <li key={t.id} style={{ borderTop: rowBorder }}>
                        <button onClick={() => setOpenId(openId === t.id ? null : t.id)} aria-expanded={openId === t.id}
                            style={{ width: '100%', background: 'none', border: 0, padding: '10px 0', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', textAlign: 'left', color: 'var(--text-main)' }}>
                            <span>
                                <span>{t.title}</span>
                                <span style={{ display: 'block', ...muted }}>{t.ownerName} · {t.typeLabel}{!t.required ? ' · optional' : ''}</span>
                            </span>
                            <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                                {['PENDING', 'SENT_BACK'].includes(t.status) && <span style={{ fontSize: 13, color: t.overdue ? '#ef4444' : 'var(--text-muted)' }}>{dueText(t)}</span>}
                                <TaskStatus status={t.status} />
                            </span>
                        </button>
                        {openId === t.id && <div style={{ paddingLeft: 12 }}><TaskPanel taskId={t.id} hr say={say} onChanged={refresh} /></div>}
                    </li>
                ))}
            </ul>
            {c.status === 'OPEN' && <button style={{ ...ghost, marginTop: 8 }} onClick={cancel}><Ban size={14} /> Cancel this checklist</button>}
            {c.cancelReason && <p style={muted}>Cancelled: {c.cancelReason}</p>}
        </div>
    );
};

const CasesAdmin = () => {
    const [params] = useSearchParams();
    const [message, setMessage] = useState(null);
    const [users, setUsers] = useState([]);
    const [cases, setCases] = useState(null);
    const [status, setStatus] = useState('OPEN');
    const [openId, setOpenId] = useState(params.get('case'));
    const [starting, setStarting] = useState(false);
    const say = useCallback((ok, text) => setMessage({ ok, text }), []);
    const load = useCallback(() => {
        api.get('/lifecycle/cases', { params: status ? { status } : {} }).then(r => setCases(r.data.cases)).catch(e => say(false, errorText(e, 'Could not load checklists')));
    }, [status, say]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => { api.get('/admin/users').then(r => setUsers(r.data)).catch(e => say(false, errorText(e, 'Could not load the employee list'))); }, [say]);

    return (
        <div className="container" style={{ padding: '2rem 1rem', maxWidth: 1100 }}>
            <PageHeader crumbs={crumbsFor('joinersLeavers')} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h1 style={{ marginBottom: 4 }}>Joiners & leavers</h1>
                    <p style={{ ...muted, fontSize: 15, margin: 0 }}>Onboarding and exit checklists in progress, and what needs your review.</p>
                </div>
                <button className="btn btn-primary" style={primary} aria-expanded={starting} onClick={() => setStarting(!starting)}><Play size={14} /> Start a checklist</button>
            </div>
            <Message message={message} />
            <JoinersPanel users={users} say={say} onChanged={load} />
            <ExitsPanel users={users} say={say} />
            {starting && <section className="card" style={card}><StartForm users={users} say={say} onStarted={(id) => { setStarting(false); setStatus('OPEN'); setOpenId(id); load(); }} /></section>}
            <section className="card" style={card}>
                <div role="tablist" style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                    {[['OPEN', 'In progress'], ['COMPLETED', 'Completed'], ['CANCELLED', 'Cancelled']].map(([k, l]) => (
                        <button key={k} role="tab" aria-selected={status === k} style={{ ...ghost, background: status === k ? 'var(--primary)' : 'transparent', color: status === k ? 'var(--on-primary, #fff)' : 'var(--text-main)' }} onClick={() => setStatus(k)}>{l}</button>
                    ))}
                </div>
                {!cases ? <p style={muted}>Loading…</p> : cases.length === 0 ? <p style={muted}>None.</p> : (
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {cases.map(c => (
                            <li key={c.id} style={{ borderTop: rowBorder }}>
                                <button onClick={() => setOpenId(openId === c.id ? null : c.id)} aria-expanded={openId === c.id} data-testid={`case-${c.user.name}`}
                                    style={{ width: '100%', background: 'none', border: 0, padding: '12px 0', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', textAlign: 'left', color: 'var(--text-main)', flexWrap: 'wrap' }}>
                                    <span style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                                        {openId === c.id ? <ChevronDown size={16} style={{ marginTop: 3 }} /> : <ChevronRight size={16} style={{ marginTop: 3 }} />}
                                        <span>
                                            <strong>{c.user.name}</strong> <span style={muted}>· {KIND[c.kind]} · {c.templateName}</span>
                                            <span style={{ display: 'block', ...muted }}>{c.kind === 'ONBOARDING' ? 'Joins' : 'Last day'} {new Date(`${c.anchorDate}T00:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })}</span>
                                        </span>
                                    </span>
                                    <span style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 13 }}>
                                        {c.toReview > 0 && <span style={{ color: '#8b5cf6', fontWeight: 600 }}>{c.toReview} to review</span>}
                                        {c.overdue > 0 && <span style={{ color: '#ef4444' }}>{c.overdue} overdue</span>}
                                        <span aria-label={`${c.progress.done} of ${c.progress.total} required tasks done`}>
                                            <span style={{ display: 'inline-block', width: 90, height: 6, borderRadius: 3, background: 'var(--border)', verticalAlign: 'middle', marginRight: 6, overflow: 'hidden' }}>
                                                <span style={{ display: 'block', height: '100%', width: `${c.progress.total ? (100 * c.progress.done) / c.progress.total : 100}%`, background: '#10b981' }} />
                                            </span>
                                            {c.progress.done}/{c.progress.total}
                                        </span>
                                    </span>
                                </button>
                                {openId === c.id && <CaseDetail id={c.id} say={say} onChanged={load} />}
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
};

export default CasesAdmin;
